import {
  DisabledReason,
  FormValueControl,
  ValidationError,
  WithOptionalField,
} from '@angular/forms/signals';
import {
  ChangeDetectionStrategy,
  Component,
  input,
  computed,
  model,
} from '@angular/core';
import { match } from 'ts-pattern';
import type { PsUiSize } from '../../types';

export type InputType = 'text' | 'email' | 'tel' | 'url';

const SIZE_MAP: Record<PsUiSize, string> = {
  sm: 'form-control-sm',
  md: 'form-control-md',
  lg: 'form-control-lg',
};

@Component({
  selector: 'ps-text-input',
  standalone: true,
  templateUrl: './text-input.component.html',
  styleUrl: './text-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextInputComponent implements FormValueControl<string> {
  // Core state
  public readonly value = model<string>(''); // Required by FormValueControl
  public readonly touched = model<boolean>(false);

  // Availability state
  public readonly disabled = input<boolean>(false);
  public readonly disabledReasons = input<
    readonly WithOptionalField<DisabledReason>[]
  >([]);
  public readonly readonly = input<boolean>(false);

  // Validation state
  public readonly invalid = input<boolean>(false);
  public readonly errors = input<readonly WithOptionalField<ValidationError>[]>(
    [],
  );

  // Constraints
  public readonly required = input<boolean>(false);
  public readonly maxLength = input<number>();
  public readonly minLength = input<number | undefined>(0);

  // Identity
  public readonly name = input.required<string>();
  public readonly id = input.required<string>();

  // Component-specific properties
  public readonly type = input<InputType>('text');
  public readonly size = input<PsUiSize>('md');
  public readonly label = input<string>();
  public readonly ariaLabel = input<string>();
  public readonly placeholder = input<string>('');

  protected readonly ariaDescribedBy = computed<string>(
    () => `text-input-${this.id()}-errors`,
  );

  protected readonly isInvalid = computed(() => {
    return match({
      invalid: this.invalid(),
      touched: this.touched(),
      hasErrors: this.errors().length > 0,
    })
      .with({ touched: true, invalid: true }, () => true)
      .with({ touched: true, hasErrors: true }, () => true)
      .otherwise(() => false);
  });

  protected readonly inputClasses = computed(() => {
    return [
      'form-control',
      SIZE_MAP[this.size()],
      this.isInvalid() ? 'is-invalid' : '',
    ];
  });

  protected onInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.value.set(inputElement.value);
  }

  protected onBlur(): void {
    this.touched.set(true);
  }
}
